<template>
    <v-container class="my-5">
        <v-layout row wrap class="justify-center">
            <v-flex xs12>
                <!-- Header -->
                <v-card-title class="mb-4">
                    <v-btn icon @click="$router.go(-1)" class="mr-3">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <span class="headline font-weight-bold">Garaje de Carritos - Gestión de Flotilla</span>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text @click="openMaintenanceDialog" class="mr-2">
                        <v-icon left>mdi-wrench</v-icon>
                        Registro Mantenimiento
                    </v-btn>
                </v-card-title>

                <!-- Tabs -->
                <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="mb-4">
                    <v-tab>
                        <v-icon left>mdi-car</v-icon>
                        Inventario de Flotilla
                    </v-tab>
                    <v-tab>
                        <v-icon left>mdi-map</v-icon>
                        Mapa Interactivo
                    </v-tab>
                    <v-tab>
                        <v-icon left>mdi-battery-charging</v-icon>
                        Zona de Carga
                    </v-tab>
                    <v-tab>
                        <v-icon left>mdi-history</v-icon>
                        Historial
                    </v-tab>
                </v-tabs>

                <v-tabs-items v-model="activeTab" class="transparent">
                    <!-- Tab 1: Inventario de Flotilla -->
                    <v-tab-item>
                        <!-- Estadísticas -->
                        <v-row class="mb-4">
                            <v-col cols="12" sm="6" md="3">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold primary--text">{{ totalCars }}</div>
                                    <div class="caption grey--text">Total de Unidades</div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold green--text">{{ availableCars }}</div>
                                    <div class="caption grey--text">Disponibles</div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold orange--text">{{ inMaintenanceCars }}</div>
                                    <div class="caption grey--text">En Mantenimiento</div>
                                </v-card>
                            </v-col>
                            <v-col cols="12" sm="6" md="3">
                                <v-card class="text-center pa-3">
                                    <div class="display-1 font-weight-bold info--text">{{ chargingCars }}</div>
                                    <div class="caption grey--text">En Carga</div>
                                </v-card>
                            </v-col>
                        </v-row>

                        <!-- Tabla de Flotilla -->
                        <v-card class="elevation-1 mb-4">
                            <v-card-subtitle class="pa-4">
                                <v-icon small class="mr-2">mdi-car-multiple</v-icon>
                                Inventario de Vehículos
                            </v-card-subtitle>
                            <v-data-table
                                :headers="fleetHeaders"
                                :items="fleet"
                                class="elevation-0"
                                :no-data-text="'Sin vehículos registrados'">

                                <template v-slot:item.unit_number="{ item }">
                                    <div class="font-weight-bold">{{ item.unit_number }}</div>
                                    <div class="caption grey--text">{{ item.brand_model }}</div>
                                </template>

                                <template v-slot:item.status="{ item }">
                                    <v-chip small :color="getStatusColor(item.status)" text-color="white">
                                        {{ getStatusLabel(item.status) }}
                                    </v-chip>
                                </template>

                                <template v-slot:item.battery="{ item }">
                                    <div class="font-weight-bold">{{ item.battery_level }}%</div>
                                    <v-progress-linear :value="item.battery_level" :color="getBatteryColor(item.battery_level)" class="mt-1"></v-progress-linear>
                                </template>

                                <template v-slot:item.slot="{ item }">
                                    <v-chip small outlined>{{ item.slot }}</v-chip>
                                </template>

                                <template v-slot:item.actions="{ item }">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn
                                                icon
                                                small
                                                v-bind="attrs"
                                                v-on="on"
                                                @click="openEditDialog(item)">
                                                <v-icon small>mdi-pencil</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Editar</span>
                                    </v-tooltip>
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on" @click="viewDetails(item)">
                                                <v-icon small>mdi-eye</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Ver detalles</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-tab-item>

                    <!-- Tab 2: Mapa Interactivo -->
                    <v-tab-item>
                        <v-card class="elevation-1">
                            <v-card-subtitle class="pa-4">
                                <v-icon small class="mr-2">mdi-map</v-icon>
                                Disposición de Cajones y Zona de Carga
                            </v-card-subtitle>
                            <v-card-text>
                                <v-row>
                                    <!-- Mapa visual de slots -->
                                    <v-col cols="12" md="8">
                                        <div class="map-container pa-4">
                                            <div class="subtitle-2 font-weight-bold mb-4">Taller de Mantenimiento</div>
                                            <div class="slots-grid">
                                                <div
                                                    v-for="slot in generateSlots(12)"
                                                    :key="slot"
                                                    class="slot-box"
                                                    :class="{
                                                        occupied: isSlotOccupied(slot),
                                                        maintenance: isSlotInMaintenance(slot)
                                                    }"
                                                    @click="showSlotInfo(slot)">
                                                    <div class="slot-number">{{ slot }}</div>
                                                    <div v-if="isSlotOccupied(slot)" class="slot-vehicle">
                                                        {{ getVehicleInSlot(slot).unit_number }}
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="subtitle-2 font-weight-bold mt-6 mb-4">Zona de Carga</div>
                                            <div class="charge-zone">
                                                <div class="charging-slots">
                                                    <div
                                                        v-for="i in 4"
                                                        :key="`charge-${i}`"
                                                        class="charge-slot"
                                                        :class="{ active: isChargingSlotActive(i) }">
                                                        <v-icon>mdi-battery-charging</v-icon>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </v-col>

                                    <!-- Leyenda -->
                                    <v-col cols="12" md="4">
                                        <v-card outlined class="pa-4">
                                            <div class="subtitle-2 font-weight-bold mb-4">Leyenda</div>
                                            <div class="mb-3">
                                                <div class="d-flex align-center">
                                                    <div class="legend-box available mr-2"></div>
                                                    <span class="caption">Disponible</span>
                                                </div>
                                            </div>
                                            <div class="mb-3">
                                                <div class="d-flex align-center">
                                                    <div class="legend-box occupied mr-2"></div>
                                                    <span class="caption">Ocupado</span>
                                                </div>
                                            </div>
                                            <div class="mb-3">
                                                <div class="d-flex align-center">
                                                    <div class="legend-box maintenance mr-2"></div>
                                                    <span class="caption">En Mantenimiento</span>
                                                </div>
                                            </div>
                                            <div>
                                                <div class="d-flex align-center">
                                                    <div class="legend-box charging mr-2"></div>
                                                    <span class="caption">En Carga</span>
                                                </div>
                                            </div>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                        </v-card>
                    </v-tab-item>

                    <!-- Tab 3: Zona de Carga -->
                    <v-tab-item>
                        <v-card class="elevation-1 mb-4">
                            <v-card-subtitle class="pa-4">
                                <v-icon small class="mr-2">mdi-battery-charging</v-icon>
                                Monitoreo de Baterías - Zona de Carga
                            </v-card-subtitle>
                            <v-data-table
                                :headers="chargeHeaders"
                                :items="chargingUnits"
                                class="elevation-0"
                                :no-data-text="'Sin vehículos en carga'">

                                <template v-slot:item.unit_number="{ item }">
                                    <div class="font-weight-bold">{{ item.unit_number }}</div>
                                    <div class="caption grey--text">{{ item.brand_model }}</div>
                                </template>

                                <template v-slot:item.battery_level="{ item }">
                                    <div class="font-weight-bold">{{ item.battery_level }}%</div>
                                    <v-progress-linear :value="item.battery_level" :color="getBatteryColor(item.battery_level)" class="mt-1"></v-progress-linear>
                                </template>

                                <template v-slot:item.charge_start="{ item }">
                                    <div class="caption">{{ item.charge_start }}</div>
                                </template>

                                <template v-slot:item.actions="{ item }">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on" @click="removeFromCharge(item)" :disabled="item.battery_level < 100">
                                                <v-icon small>mdi-check</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ item.battery_level === 100 ? 'Marcar como cargado' : 'Esperar a 100%' }}</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-tab-item>

                    <!-- Tab 4: Historial -->
                    <v-tab-item>
                        <v-card class="elevation-1">
                            <v-card-subtitle class="pa-4">
                                <v-icon small class="mr-2">mdi-history</v-icon>
                                Historial de Movimientos y Mantenimiento
                            </v-card-subtitle>
                            <v-data-table
                                :headers="historyHeaders"
                                :items="movementHistory"
                                class="elevation-0"
                                :items-per-page="10"
                                :no-data-text="'Sin historial'"
                                sort-by="date"
                                :sort-desc="true">

                                <template v-slot:item.date="{ item }">
                                    <div class="font-weight-bold">{{ formatDate(item.date) }}</div>
                                    <div class="caption grey--text">{{ item.time }}</div>
                                </template>

                                <template v-slot:item.unit_number="{ item }">
                                    <div class="font-weight-bold">{{ item.unit_number }}</div>
                                </template>

                                <template v-slot:item.action="{ item }">
                                    <v-chip small :color="getActionColor(item.action)" text-color="white">
                                        {{ getActionLabel(item.action) }}
                                    </v-chip>
                                </template>

                                <template v-slot:item.details="{ item }">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon x-small v-bind="attrs" v-on="on" @click="viewHistory(item)">
                                                <v-icon x-small>mdi-eye</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Ver detalles</span>
                                    </v-tooltip>
                                </template>
                            </v-data-table>
                        </v-card>
                    </v-tab-item>
                </v-tabs-items>
            </v-flex>
        </v-layout>

        <!-- Diálogo: Editar Vehículo -->
        <v-dialog v-model="editDialog" max-width="700px" persistent>
            <v-card v-if="currentVehicle">
                <v-card-title>
                    <div>
                        <div class="headline">Editar Unidad</div>
                        <div class="caption grey--text">{{ currentVehicle.unit_number }} - {{ currentVehicle.brand_model }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="editDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-form ref="editForm" v-model="validEdit" lazy-validation>
                        <div class="subtitle-2 font-weight-bold mb-4">Información General</div>
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="currentVehicle.unit_number"
                                    label="Número de Unidad"
                                    outlined
                                    dense
                                    disabled
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model="currentVehicle.slot"
                                    label="Cajón Asignado"
                                    outlined
                                    dense
                                    type="number"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-divider class="my-4"></v-divider>

                        <div class="subtitle-2 font-weight-bold mb-4">Estado y Mantenimiento</div>
                        <v-row>
                            <v-col cols="12" sm="6">
                                <v-select
                                    v-model="currentVehicle.status"
                                    :items="statusOptions"
                                    label="Estado"
                                    outlined
                                    dense
                                ></v-select>
                            </v-col>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    v-model.number="currentVehicle.battery_level"
                                    label="Nivel de Batería (%)"
                                    type="number"
                                    outlined
                                    dense
                                    min="0"
                                    max="100"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <v-row>
                            <v-col cols="12">
                                <v-select
                                    v-model="currentVehicle.maintenance_shop"
                                    :items="maintenanceShops"
                                    label="Taller de Mantenimiento"
                                    outlined
                                    dense
                                ></v-select>
                            </v-col>
                        </v-row>

                        <v-divider class="my-4"></v-divider>

                        <div class="subtitle-2 font-weight-bold mb-4">Observaciones</div>
                        <v-textarea
                            v-model="currentVehicle.notes"
                            label="Notas del Vehículo"
                            outlined
                            dense
                            rows="3"
                            placeholder="Observaciones sobre el estado o historial"
                            counter
                            maxlength="300"
                        ></v-textarea>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="editDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" text @click="saveVehicle" :disabled="!validEdit">
                        <v-icon left>mdi-content-save</v-icon>
                        Guardar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Detalles del Vehículo -->
        <v-dialog v-model="detailsDialog" max-width="600px">
            <v-card v-if="viewingVehicle">
                <v-card-title>
                    <div>
                        <div class="headline">Detalles de Unidad</div>
                        <div class="caption grey--text">{{ viewingVehicle.unit_number }}</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="detailsDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-list dense>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Número de Unidad</div>
                                <div class="body-2 font-weight-bold">{{ viewingVehicle.unit_number }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Marca/Modelo</div>
                                <div class="body-2 font-weight-bold">{{ viewingVehicle.brand_model }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Estado</div>
                                <v-chip small :color="getStatusColor(viewingVehicle.status)" text-color="white">
                                    {{ getStatusLabel(viewingVehicle.status) }}
                                </v-chip>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Cajón Asignado</div>
                                <div class="body-2 font-weight-bold">{{ viewingVehicle.slot }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="my-2"></v-divider>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Nivel de Batería</div>
                                <div class="body-2 font-weight-bold">{{ viewingVehicle.battery_level }}%</div>
                                <v-progress-linear :value="viewingVehicle.battery_level" :color="getBatteryColor(viewingVehicle.battery_level)" class="mt-2"></v-progress-linear>
                            </v-list-item-content>
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-content>
                                <div class="caption grey--text">Taller de Mantenimiento</div>
                                <div class="body-2">{{ viewingVehicle.maintenance_shop }}</div>
                            </v-list-item-content>
                        </v-list-item>
                        <v-divider class="my-2"></v-divider>
                        <v-list-item v-if="viewingVehicle.notes">
                            <v-list-item-content>
                                <div class="caption grey--text">Observaciones</div>
                                <div class="body-2">{{ viewingVehicle.notes }}</div>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Diálogo: Registro de Mantenimiento -->
        <v-dialog v-model="maintenanceDialog" max-width="600px">
            <v-card>
                <v-card-title>
                    <div>
                        <div class="headline">Registro de Mantenimiento</div>
                        <div class="caption grey--text">Registrar tareas de mantenimiento</div>
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon @click="maintenanceDialog = false" class="cursor-pointer">mdi-close</v-icon>
                </v-card-title>

                <v-card-text>
                    <v-form ref="maintenanceForm" v-model="validMaintenance" lazy-validation>
                        <v-select
                            v-model="maintenanceRecord.unit_number"
                            :items="fleetUnits"
                            label="Unidad"
                            outlined
                            dense
                            :rules="[v => v || 'Se requiere una unidad']"
                        ></v-select>
                        <v-select
                            v-model="maintenanceRecord.maintenance_type"
                            :items="maintenanceTypes"
                            label="Tipo de Mantenimiento"
                            outlined
                            dense
                            :rules="[v => v || 'Se requiere un tipo']"
                            class="mt-4"
                        ></v-select>
                        <v-textarea
                            v-model="maintenanceRecord.description"
                            label="Descripción"
                            outlined
                            dense
                            rows="3"
                            placeholder="Detalles del mantenimiento realizado"
                            counter
                            maxlength="300"
                            class="mt-4"
                            :rules="[v => v || 'Se requiere una descripción']"
                        ></v-textarea>
                    </v-form>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="maintenanceDialog = false">Cancelar</v-btn>
                    <v-btn color="primary" text @click="saveMaintenance" :disabled="!validMaintenance">
                        <v-icon left>mdi-content-save</v-icon>
                        Guardar
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000" top>
            {{ snackbarMessage }}
        </v-snackbar>
    </v-container>
</template>

<script>
import { garageAPI } from '@/services/garage'

export default {
  name: 'Garage',
  data: () => ({
    activeTab: 0,
    editDialog: false,
    detailsDialog: false,
    maintenanceDialog: false,
    validEdit: false,
    validMaintenance: false,
    snackbar: false,
    snackbarMessage: '',
    snackbarColor: 'success',
    currentVehicle: null,
    viewingVehicle: null,
    loading: false,
    fleetHeaders: [
      { text: 'Unidad / Modelo', value: 'unit_number', width: '20%' },
      { text: 'Estado', value: 'status', width: '15%' },
      { text: 'Batería', value: 'battery', width: '20%' },
      { text: 'Cajón', value: 'slot', width: '10%', align: 'center' },
      { text: 'Acciones', value: 'actions', width: '15%', sortable: false, align: 'center' }
    ],
    chargeHeaders: [
      { text: 'Unidad / Modelo', value: 'unit_number', width: '25%' },
      { text: 'Nivel Batería', value: 'battery_level', width: '25%' },
      { text: 'Inicio Carga', value: 'charge_start', width: '25%' },
      { text: 'Acciones', value: 'actions', width: '15%', sortable: false, align: 'center' }
    ],
    historyHeaders: [
      { text: 'Fecha / Hora', value: 'date', width: '20%' },
      { text: 'Unidad', value: 'unit_number', width: '20%' },
      { text: 'Acción', value: 'action', width: '25%' },
      { text: 'Detalles', value: 'details', width: '15%', sortable: false, align: 'center' }
    ],
    statusOptions: ['disponible', 'mantenimiento', 'carga', 'no_disponible'],
    maintenanceShops: ['Taller A', 'Taller B', 'Taller C', 'Interno'],
    maintenanceTypes: ['Preventivo', 'Correctivo', 'Batería', 'Revisión General', 'Reparación'],
    maintenanceRecord: {
      unit_number: '',
      maintenance_type: '',
      description: ''
    },
    fleet: [],
    movementHistory: []
  }),
  computed: {
    totalCars () {
      return this.fleet.length
    },
    availableCars () {
      return this.fleet.filter(c => c.status === 'disponible').length
    },
    inMaintenanceCars () {
      return this.fleet.filter(c => c.status === 'mantenimiento').length
    },
    chargingCars () {
      return this.fleet.filter(c => c.status === 'carga').length
    },
    chargingUnits () {
      return this.fleet.filter(c => c.status === 'carga')
    },
    fleetUnits () {
      return this.fleet.map(c => c.unit_number)
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    async loadData () {
      this.loading = true
      try {
        await Promise.all([
          this.loadVehicles(),
          this.loadMovementHistory()
        ])
      } catch (error) {
        console.error('Error loading garage data:', error)
        this.showSnackbar('Error al cargar datos', 'error')
      } finally {
        this.loading = false
      }
    },
    async loadVehicles () {
      try {
        const response = await garageAPI.getVehicles()
        this.fleet = response.results || []
      } catch (error) {
        console.error('Error loading vehicles:', error)
        this.showSnackbar('Error al cargar vehículos', 'error')
      }
    },
    async loadMovementHistory () {
      try {
        const response = await garageAPI.getMovements({ limit: 50, sort_by: '-date' })
        this.movementHistory = response.results || []
      } catch (error) {
        console.error('Error loading movements:', error)
        this.showSnackbar('Error al cargar histórico', 'error')
      }
    },
    showSnackbar (message, color) {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    },
    getStatusColor (status) {
      const colors = {
        disponible: 'green',
        mantenimiento: 'orange',
        carga: 'info',
        no_disponible: 'red'
      }
      return colors[status] || 'grey'
    },
    getStatusLabel (status) {
      const labels = {
        disponible: 'Disponible',
        mantenimiento: 'Mantenimiento',
        carga: 'En Carga',
        no_disponible: 'No Disponible'
      }
      return labels[status] || 'Desconocido'
    },
    getActionColor (action) {
      const colors = {
        salida: 'primary',
        retorno: 'success',
        inicio_carga: 'info',
        mantenimiento: 'orange'
      }
      return colors[action] || 'grey'
    },
    getActionLabel (action) {
      const labels = {
        salida: 'Salida de Garaje',
        retorno: 'Retorno',
        inicio_carga: 'Inicio Carga',
        mantenimiento: 'Mantenimiento'
      }
      return labels[action] || 'Desconocido'
    },
    getBatteryColor (level) {
      if (level >= 75) return 'success'
      if (level >= 50) return 'warning'
      if (level >= 25) return 'orange'
      return 'error'
    },
    formatDate (dateStr) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    openEditDialog (vehicle) {
      this.currentVehicle = { ...vehicle }
      this.editDialog = true
    },
    async saveVehicle () {
      if (this.$refs.editForm.validate()) {
        try {
          const vehicleData = {
            status: this.currentVehicle.status,
            battery_level: this.currentVehicle.battery_level,
            slot: this.currentVehicle.slot,
            maintenance_shop: this.currentVehicle.maintenance_shop,
            notes: this.currentVehicle.notes
          }
          await garageAPI.updateVehicle(this.currentVehicle.id, vehicleData)
          this.showSnackbar('Vehículo actualizado correctamente', 'success')
          this.editDialog = false
          this.loadVehicles()
        } catch (error) {
          console.error('Error saving vehicle:', error)
          const message = error.response?.data?.message || 'Error al guardar el vehículo'
          this.showSnackbar(message, 'error')
        }
      }
    },
    viewDetails (vehicle) {
      this.viewingVehicle = { ...vehicle }
      this.detailsDialog = true
    },
    viewHistory (record) {
      this.snackbarMessage = `Detalles: ${record.details}`
      this.snackbar = true
    },
    openMaintenanceDialog () {
      this.maintenanceRecord = {
        unit_number: '',
        maintenance_type: '',
        description: ''
      }
      this.maintenanceDialog = true
    },
    async saveMaintenance () {
      if (this.$refs.maintenanceForm.validate()) {
        try {
          const maintenanceData = {
            unit_number: this.maintenanceRecord.unit_number,
            maintenance_type: this.maintenanceRecord.maintenance_type,
            description: this.maintenanceRecord.description
          }
          await garageAPI.createMaintenance(maintenanceData)
          this.showSnackbar('Mantenimiento registrado correctamente', 'success')
          this.maintenanceDialog = false
          this.loadData()
        } catch (error) {
          console.error('Error saving maintenance:', error)
          const message = error.response?.data?.message || 'Error al registrar el mantenimiento'
          this.showSnackbar(message, 'error')
        }
      }
    },
    async removeFromCharge (vehicle) {
      try {
        const vehicleData = {
          status: 'disponible'
        }
        await garageAPI.updateVehicle(vehicle.id, vehicleData)
        this.showSnackbar(`${vehicle.unit_number} marcado como cargado y disponible`, 'success')
        this.loadVehicles()
      } catch (error) {
        console.error('Error removing vehicle from charge:', error)
        const message = error.response?.data?.message || 'Error al actualizar vehículo'
        this.showSnackbar(message, 'error')
      }
    },
    generateSlots (count) {
      return Array.from({ length: count }, (_, i) => i + 1)
    },
    isSlotOccupied (slot) {
      return this.fleet.some(v => v.slot === slot && v.status !== 'mantenimiento')
    },
    isSlotInMaintenance (slot) {
      return this.fleet.some(v => v.slot === slot && v.status === 'mantenimiento')
    },
    getVehicleInSlot (slot) {
      return this.fleet.find(v => v.slot === slot) || { unit_number: '' }
    },
    isChargingSlotActive (slotNumber) {
      return this.chargingCars >= slotNumber
    },
    showSlotInfo (slot) {
      const vehicle = this.getVehicleInSlot(slot)
      if (vehicle.id) {
        this.viewingVehicle = { ...vehicle }
        this.detailsDialog = true
      }
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.map-container {
  background: #f5f5f5;
  border-radius: 8px;
}

.slots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
}

.slot-box {
  background: #fff;
  border: 2px solid #4CAF50;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.slot-box:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.slot-box.occupied {
  background: #E3F2FD;
  border-color: #2196F3;
}

.slot-box.maintenance {
  background: #FFF3E0;
  border-color: #FF9800;
}

.slot-number {
  font-size: 12px;
  color: #999;
  font-weight: bold;
}

.slot-vehicle {
  font-size: 14px;
  color: #1976D2;
  font-weight: bold;
  margin-top: 4px;
}

.charge-zone {
  background: #fff;
  border: 2px dashed #2196F3;
  border-radius: 8px;
  padding: 20px;
}

.charging-slots {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}

.charge-slot {
  width: 60px;
  height: 60px;
  background: #E3F2FD;
  border: 2px solid #BBDEFB;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  transition: all 0.3s ease;
}

.charge-slot.active {
  background: #90CAF9;
  border-color: #2196F3;
  color: #1976D2;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
  }
}

.legend-box {
  width: 20px;
  height: 20px;
  border-radius: 4px;
}

.legend-box.available {
  background: #4CAF50;
  border: 2px solid #4CAF50;
}

.legend-box.occupied {
  background: #E3F2FD;
  border: 2px solid #2196F3;
}

.legend-box.maintenance {
  background: #FFF3E0;
  border: 2px solid #FF9800;
}

.legend-box.charging {
  background: #90CAF9;
  border: 2px solid #2196F3;
}
</style>
